import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useAuth0 } from '@auth0/auth0-react';
import { Eye, Pencil, Play, Trash, Stop } from 'phosphor-react';
import { statusCheck } from 'utils/statusCheck';
import { useConfig } from 'components/providers/Config';
import { RowStatus } from 'components/RowStatus';
import { Progress } from 'components/Progress';

import * as S from './Styles';

export const ActionCell = row => {
    const Router = useRouter();
    const config = useConfig();
    const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

    return (
        <S.TableCell>
            {['created'].includes(row.status) && (
                <Link href={`/jobs/${row.id}`}>
                    <S.TableAction>
                        <Eye size={18} />
                    </S.TableAction>
                </Link>
            )}

            {['created'].includes(row.status) && (
                <Link href={`/jobs/${row.id}/edit`}>
                    <S.TableAction>
                        <Pencil size={18}  />
                    </S.TableAction>
                </Link>
            )}

            {['active', 'pending'].includes(row.status) && (
                <S.TableCell>
                    <S.TableAction title="Stop job" onClick={async event => {
                        event.preventDefault();

                        if (confirm(`Are you sure you want to stop "${row.name}" running`)) {
                            try {
                                const accessToken = await getAccessTokenSilently();

                                const res = await fetch(`${config.api.API_HOST}/jobs/${row.id}/cancel`, {
                                    method: 'PATCH',
                                    headers: {
                                        Authorization: 'Bearer ' + accessToken
                                    }
                                });

                                if (statusCheck(res, Router, loginWithRedirect)) {
                                    return Router.reload();
                                }

                                throw new Error('Job cancellation failed');
                            } catch (error) {
                                console.error(error);
                            }
                        }
                    }}>
                        <Stop size={18} weight="fill" />
                    </S.TableAction>
                </S.TableCell>
            )}

            {['created'].includes(row.status) && (
                <S.TableAction onClick={async (event) => {
                    event.preventDefault();

                    if (confirm(`Are you sure you want to delete "${row.name}"`)) {
                        try {
                            const accessToken = await getAccessTokenSilently();

                            const res = await fetch(`${config.api.API_HOST}/jobs/${row.id}/delete`, {
                                method: 'DELETE',
                                headers: {
                                    Authorization: 'Bearer ' + accessToken
                                }
                            });

                            if (statusCheck(res, Router, loginWithRedirect)) {
                                return Router.reload();
                            }

                            throw new Error('Job deletion failed');
                        } catch (error) {
                            console.error(error);
                        }
                    }
                }}>
                    <Trash size={18} />
                </S.TableAction>
            )}

            {['created'].includes(row.status) && (
                <Link href={`/jobs/${row.id}/confirm`}>
                    <S.TableAction>
                        <Play size={18} weight="fill" />
                    </S.TableAction>
                </Link>
            )}
        </S.TableCell>
    );
}

const options = {
    publish: 'Do not copy publications and status',
    description: 'Do not copy description',
    price: 'Do not copy prices',
    variants: 'Do not copy variants',
    media: 'Do not copy media',
    seo: 'Do not copy metafields',
    tags: 'Do not copy tags',
    title: 'Do not copy title',
    variantTitle: 'Do not override variant title',
    translatedContent: 'Do not copy translated content',
};

export const columns = stores => [
    {
        name: 'Title',
        cell: row => <S.TableCell>{row.name}</S.TableCell>
    },
    {
        name: 'Source',
        cell: row => <S.TableCell>{stores[row.sourceStore]?.name || row.sourceStore}</S.TableCell>
    },
    {
        name: 'Destinations',
        cell: row => (
            <S.TableCell>
                {row.destinationStores.map((store: string) => <div key={store}>{stores[store]?.name || store}</div>)}
            </S.TableCell>
        )
    },
    {
        name: 'Product count',
        grow: '0',
        cell: row => <S.TableCell>{row?.productIDs?.length ?? '0'}</S.TableCell>
    },
    {
        name: 'Options',
        cell: row => (
            <S.TableCell>
                {Object.keys(row.options).filter(key => ['publish'].includes(key) ? !row.options[key] : row.options[key]).length > 0 ?
                    Object.keys(row.options).map((option: string) => {
                        if (['publish'].includes(option)) {
                            return !row.options[option] && <div key={option}>{options[option]}</div>
                        }

                        return row.options[option] && <div key={option}>{options[option]}</div>
                    })
                 : 'N/A'}
            </S.TableCell>
        )
    },
    {
        name: 'Status',
        cell: row => <RowStatus status={row.status} />
    },
    {
        name: 'Progress',
        cell: row => (
            <>
                {row?.progress && Object.keys(row.progress).length > 0 ? (
                    <Progress progress={row.progress} />
                ) : '----'}
            </>
        )
    },
    {
        name: 'Actions',
        width: '12rem',
        cell: ActionCell
    },
];
